import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import styled from 'styled-components'

import { Point } from '../../../api/types'
import { ViewpointFlatImage } from '../../../api/viewpointFlatImages'
import { areQualityFeaturesEnabled } from '../../../config/qualityFeatures'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { TagProvider } from '../../../contexts/tagContext'
import { useUserContext } from '../../../contexts/userContext'
import {
  PATH_STRINGS,
  useGeneratedPaths,
} from '../../../hooks/useGeneratedPaths'
import { useNavigation } from '../../../hooks/useNavigation'
import { media, isMobileViewport } from '../../../styles/breakpoints'
import { selectImagesForDateRange } from '../../../utils/selectImagesForDateRange'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { MapPoint } from '../../common/MapViewer'
import { PermissionRoute } from '../../common/PermissionRoute/PermissionRoute'
import {
  PermissionWrapper,
  checkUserPermissions,
} from '../../common/PermissionWrapper/PermissionWrapper'
import {
  IViewSelectorOption,
  OptionPicker,
} from '../../common/ViewSelector/components/OptionPicker'
import { ViewSelector } from '../../common/ViewSelector/ViewSelector'
import { ImageViewer } from '../image_viewer/ImageViewer'
import { ReportsPage } from '../reports/ReportsPage'
import { ReviewTaskFloorView } from '../review/ReviewTaskFloorView'
import { ReviewTaskList } from '../review/ReviewTaskList'
import { ReviewTaskWorkspace } from '../review/ReviewTaskWorkspace'

const ProgressPage = lazy(() =>
  import('../progress/ProgressPage').then(loadedModule => ({
    default: loadedModule.ProgressPage,
  })),
)
const SafetyPage = lazy(() =>
  import('../safety/SafetyPage').then(loadedModule => ({
    default: loadedModule.SafetyPage,
  })),
)
const InspectionPage = lazy(() =>
  import('../inspection/InspectionPage').then(loadedModule => ({
    default: loadedModule.InspectionPage,
  })),
)
const QualityPage = lazy(() =>
  import('../quality/QualityPage').then(loadedModule => ({
    default: loadedModule.QualityPage,
  })),
)
const ReportGeneratorPage = lazy(() =>
  import('../reports/ReportGeneratorPage').then(loadedModule => ({
    default: loadedModule.ReportGeneratorPage,
  })),
)
const ReportTypeChooserPage = lazy(() =>
  import('../reports/ReportTypeChooserPage').then(m => ({
    default: m.ReportTypeChooserPage,
  })),
)
const ProgressReportGeneratorPage = lazy(() =>
  import('../reports/ProgressReportGeneratorPage').then(m => ({
    default: m.ProgressReportGeneratorPage,
  })),
)

import { BuildingOverview } from './components/BuildingOverview/BuildingOverview'
import { FlatImagePanel } from './components/FlatImagePanel'
import { FloorPlanView } from './components/FloorPlanView'
import { PreviewPopup } from './components/PreviewPopup'
import { ViewModeMenu } from './components/ViewModeMenu'
import {
  useBuildingQuery,
  useFloorImages,
  useImagesQuery,
} from './hooks/buildingQueries'
import { useRouteData } from './hooks/useRouteData'

export const BuildingPage = () => {
  const [hoverFloor, setHoverFloor] = useState<string | null>(null)
  const {
    generateProjectSafetyPath,
    generateProjectPath,
    generateProjectProgressPath,
    generateProjectProgressGanttPath,
    generateProjectProgressChartsPath,
    generateProjectProgressReportsPath,
    generateProjectProgressAlertsPath,
    generateProjectReportsPath,
    generateProjectReportsNewPath,
    generateProjectReportsNewSafetyPath,
    generateProjectReportsNewProgressPath,
    generateSafetyGalleryPath,
    generateSafetyAlertsPath,
    generateProjectInspectionPath,
    generateInspectionGalleryPath,
    generateProjectQualityPath,
    generateReviewOverviewPath,
    generateAdminManageReviewPath,
  } = useGeneratedPaths()

  useRouteData()
  const {
    updateBuilding,
    updateFloor,
    updatePoint,
    updateImage,
    state: buildingState,
  } = useBuildingContext()
  const { state: userState } = useUserContext()
  const { navigateToFloor, navigateToCurrentPointFullscreen } = useNavigation()
  const location = useLocation()

  const isNon360ImageFloor = !!buildingState.floorData?.is_non_360_image_floor

  const [floorListExpanded, setFloorListExpanded] = useState<boolean>(true)
  const [flatImageIndex, setFlatImageIndex] = useState(0)

  const collapseFloorListOnMobile = useCallback(() => {
    if (isMobileViewport()) {
      setFloorListExpanded(false)
    }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  const updateFloorDesc = (data: any) => {
    updateFloor(data)
  }

  const inProgressTracking = !!useRouteMatch(PATH_STRINGS.progressOverview)
  const inSafetyTracking = !!useRouteMatch(PATH_STRINGS.safetyOverview)
  const progressFloorMatch = !!useRouteMatch([
    PATH_STRINGS.progressFloor,
    PATH_STRINGS.progressFloorMulti3d,
  ])
  /** Progress floor map only needs plan + points for markers; skip heavy region images so loads (and PDF capture) finish sooner. */
  const excludeRegionPictures =
    (!inProgressTracking && !inSafetyTracking) || progressFloorMatch

  const { isLoading: floorImagesLoading, refetch: refetchFloorImages } =
    useFloorImages(
      updateFloorDesc,
      false,
      undefined,
      undefined,
      excludeRegionPictures,
    )

  const onFlatImageUploadSuccess = useCallback(() => {
    refetchFloorImages()
  }, [refetchFloorImages])

  const selectedFlatPoint = useMemo(() => {
    if (!isNon360ImageFloor || !buildingState.pointId) return null
    return buildingState.floorData?.points?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- floor point with flatImage
      (p: any) =>
        String(p.point_id) === String(buildingState.pointId) ||
        String(p.id) === String(buildingState.pointId),
    )
  }, [
    isNon360ImageFloor,
    buildingState.pointId,
    buildingState.floorData?.points,
  ])

  const selectedFlatPointImages = useMemo((): ViewpointFlatImage[] => {
    if (!selectedFlatPoint) return []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- floor point with flat images
    const point = selectedFlatPoint as any
    const images = Array.isArray(point.images)
      ? (point.images as ViewpointFlatImage[])
      : []
    if (images.length) {
      return selectImagesForDateRange(images, buildingState.dateRange)
    }
    return point.flatImage ? [point.flatImage as ViewpointFlatImage] : []
  }, [selectedFlatPoint, buildingState.dateRange])

  useEffect(() => {
    setFlatImageIndex(0)
  }, [buildingState.dateRange, buildingState.pointId, selectedFlatPointImages])

  const selectedFlatImage = useMemo(() => {
    if (!selectedFlatPointImages.length) return null
    const index = Math.min(flatImageIndex, selectedFlatPointImages.length - 1)
    return selectedFlatPointImages[index] ?? null
  }, [flatImageIndex, selectedFlatPointImages])

  const onSelectFlatImageIndex = useCallback((index: number) => {
    setFlatImageIndex(index)
  }, [])

  const selectedFlatSubPointId = useMemo(() => {
    if (!selectedFlatPoint) return null
    const subId = Number(selectedFlatPoint.point_id)
    return Number.isFinite(subId) ? subId : null
  }, [selectedFlatPoint])
  const imagesQuery = useImagesQuery(
    buildingState.dateRange,
    buildingState.imageData,
    updateImage,
    updateImage,
  )

  const { data: buildingData, isLoading: buildingLoading } =
    useBuildingQuery(updateBuilding)

  const floorPlanViewLoaded: boolean =
    buildingState.floorData && buildingState.floorData.imageUrl

  useEffect(() => {
    updateBuilding({
      floorImagesLoading: floorImagesLoading,
    })
  }, [floorImagesLoading, updateBuilding])

  /*
  if buildingState.pointId changes, update the active point info
   */
  useEffect(() => {
    const point = buildingState.floorData.points?.find(
      (point: Point) => buildingState.pointId === point.point_id.toString(),
    )
    if (!point) {
      return
    }

    updatePoint({
      ...point,
      panoramaUrl: buildingState.imageData?.image ?? point.panoramaUrl,
      panoramaThumbUrl: point.panoramaUrl,
    })
    // eslint-disable-next-line
  }, [
    buildingState.imageData,
    buildingState.pointId,
    buildingState.floorData.points,
  ])

  /*
  If the date-range changes, re-fetch the 360 image to get one within the range
   */
  useEffect(() => {
    if (imagesQuery.isSuccess) imagesQuery.refetch()
    if (isNon360ImageFloor) {
      refetchFloorImages()
    }
    // eslint-disable-next-line
  }, [buildingState.dateRange])

  const VIEW_MODE_MAP = useMemo(
    () => ({
      'Site View': {
        route: buildingState.projectId
          ? generateProjectPath(buildingState.projectId)
          : '',
        display: true,
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'project_tables.read_project',
          buildingState.projectId,
        ),
      },
      Safety: {
        route: buildingState.projectId
          ? generateProjectSafetyPath(buildingState.projectId)
          : '',
        display: buildingState.projectData.display_safety,
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'safety_tables.read_observation',
          buildingState.projectId,
        ),
        additionalActivePaths: [
          buildingState.projectId
            ? generateSafetyGalleryPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateSafetyAlertsPath(buildingState.projectId)
            : '',
        ],
      },
      Progress: {
        route: buildingState.projectId
          ? generateProjectProgressPath(buildingState.projectId)
          : '',
        display: buildingState.projectData.display_progress,
        additionalActivePaths: [
          buildingState.projectId
            ? generateProjectProgressGanttPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateProjectProgressChartsPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateProjectProgressReportsPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateProjectProgressAlertsPath(buildingState.projectId)
            : '',
        ],
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'progress_tables.read_project_tracker',
          buildingState.projectId,
        ),
      },
      Review: {
        route: buildingState.projectId
          ? generateReviewOverviewPath(buildingState.projectId)
          : '',
        display: buildingState.projectData.display_progress,
        userCanAccess:
          userState.user_type !== 'super' &&
          checkUserPermissions(
            userState.permissions,
            'progress_tables.access_progress_review',
            buildingState.projectId,
          ),
        badge: buildingState.projectData.pending_review_task_count || undefined,
        additionalActivePaths:
          buildingState.projectId &&
          location.pathname.startsWith(
            `${generateReviewOverviewPath(buildingState.projectId)}/task/`,
          )
            ? [location.pathname]
            : [],
      },
      'Quality Control': {
        route: buildingState.projectId
          ? generateProjectQualityPath(buildingState.projectId)
          : '',
        display:
          !!buildingState.projectData.display_quality &&
          areQualityFeaturesEnabled,
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'safety_tables.read_quality_observation',
          buildingState.projectId,
        ),
      },
      Reports: {
        route: buildingState.projectId
          ? generateProjectReportsPath(buildingState.projectId)
          : '',
        display: buildingState.projectData.display_reports,
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'progress_tables.read_project_tracker',
          buildingState.projectId,
        ),
        additionalActivePaths: [
          buildingState.projectId
            ? generateProjectReportsNewPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateProjectReportsNewSafetyPath(buildingState.projectId)
            : '',
          buildingState.projectId
            ? generateProjectReportsNewProgressPath(buildingState.projectId)
            : '',
        ],
      },
      Inspection: {
        route: buildingState.projectId
          ? generateProjectInspectionPath(buildingState.projectId)
          : '',
        display: buildingState.projectData.display_inspection ?? false,
        userCanAccess: checkUserPermissions(
          userState.permissions,
          'mission_tables.inspection_client',
          buildingState.projectId,
        ),
        additionalActivePaths: [
          buildingState.projectId
            ? generateInspectionGalleryPath(buildingState.projectId)
            : '',
        ],
      },
    }),
    [
      buildingState.projectData.display_progress,
      buildingState.projectData.display_safety,
      buildingState.projectData.display_quality,
      buildingState.projectData.display_reports,
      buildingState.projectData.display_inspection,
      buildingState.projectData.pending_review_task_count,
      buildingState.projectId,
      generateProjectPath,
      generateProjectProgressChartsPath,
      generateProjectProgressGanttPath,
      generateProjectProgressPath,
      generateProjectProgressReportsPath,
      generateProjectProgressAlertsPath,
      generateProjectReportsPath,
      generateProjectReportsNewPath,
      generateProjectReportsNewSafetyPath,
      generateProjectReportsNewProgressPath,
      generateProjectSafetyPath,
      generateSafetyGalleryPath,
      generateSafetyAlertsPath,
      generateProjectInspectionPath,
      generateInspectionGalleryPath,
      generateProjectQualityPath,
      generateReviewOverviewPath,
      userState.permissions,
      userState.user_type,
      location.pathname,
    ],
  )

  // Floor code to date range mapping
  const floorDateRanges = useMemo(
    () => ({
      L18: {
        min: new Date('2024-08-23T00:00:00'),
        max: new Date('2024-09-06T00:00:00'),
      },
      L17: {
        min: new Date('2024-07-02T00:00:00'),
        max: new Date('2024-07-19T00:00:00'),
      },
      L16: {
        min: new Date('2024-06-25T00:00:00'),
        max: new Date('2024-07-12T00:00:00'),
      },
      L15: {
        min: new Date('2024-06-05T00:00:00'),
        max: new Date('2024-06-17T00:00:00'),
      },
      L14: {
        min: new Date('2024-05-06T00:00:00'),
        max: new Date('2024-05-28T00:00:00'),
      },
      L13: {
        min: new Date('2024-04-25T00:00:00'),
        max: new Date('2024-05-06T00:00:00'),
      },
      L12: {
        min: new Date('2024-04-09T00:00:00'),
        max: new Date('2024-04-16T00:00:00'),
      },
      L11: {
        min: new Date('2024-02-28T00:00:00'),
        max: new Date('2024-03-20T00:00:00'),
      },
      L10: {
        min: new Date('2024-02-28T00:00:00'),
        max: new Date('2024-03-06T00:00:00'),
      },
      L9: {
        min: new Date('2024-02-07T00:00:00'),
        max: new Date('2024-02-21T00:00:00'),
      },
      L8: {
        min: new Date('2024-01-31T00:00:00'),
        max: new Date('2024-02-13T00:00:00'),
      },
      L7: {
        min: new Date('2024-01-17T00:00:00'),
        max: new Date('2024-01-24T00:00:00'),
      },
      L6: {
        min: new Date('2024-01-08T00:00:00'),
        max: new Date('2024-01-16T00:00:00'),
      },
      L5: {
        min: new Date('2023-12-19T00:00:00'),
        max: new Date('2023-12-26T00:00:00'),
      },
      L4: {
        min: new Date('2023-11-14T00:00:00'),
        max: new Date('2023-12-05T00:00:00'),
      },
      L3: {
        min: new Date('2023-11-07T00:00:00'),
        max: new Date('2023-11-21T00:00:00'),
      },
      L2: {
        min: new Date('2023-10-24T00:00:00'),
        max: new Date('2023-10-31T00:00:00'),
      },
      L1: {
        min: new Date('2024-04-23T00:00:00'),
        max: new Date('2024-06-04T00:00:00'),
      },
      GL: {
        min: new Date('2024-06-24T00:00:00'),
        max: new Date('2024-07-23T00:00:00'),
      },
      // Parking levels with dummy date ranges to hide images
      PL3: {
        min: new Date('1970-01-01T00:00:00'),
        max: new Date('1970-01-01T00:00:00'),
      },
      PL2: {
        min: new Date('1970-01-01T00:00:00'),
        max: new Date('1970-01-01T00:00:00'),
      },
      PL1: {
        min: new Date('1970-01-01T00:00:00'),
        max: new Date('1970-01-01T00:00:00'),
      },
      PGL: {
        min: new Date('1970-01-01T00:00:00'),
        max: new Date('1970-01-01T00:00:00'),
      },
      PSGL: {
        min: new Date('1970-01-01T00:00:00'),
        max: new Date('1970-01-01T00:00:00'),
      },
    }),
    [],
  )

  // Get min/max for current floor
  type FloorCode = keyof typeof floorDateRanges
  const floorCode = buildingState.floorData?.floor_code as FloorCode | undefined

  // Only restrict minDate/maxDate for specific users and projects
  const restrictedEmails = [
    'jmayse@villagegreen.com',
    'aryan@villagegreen.com',
    'test@villagegreen.com',
  ]
  const restrictedProjects = ['huyPFVy3XnQN']

  const shouldRestrictDates =
    restrictedEmails.includes(userState.email) &&
    restrictedProjects.includes(buildingState.projectId)

  const { minDate, maxDate } = useMemo(() => {
    if (shouldRestrictDates && floorCode && floorDateRanges[floorCode]) {
      return {
        minDate: floorDateRanges[floorCode].min,
        maxDate: floorDateRanges[floorCode].max,
      }
    }
    return { minDate: undefined, maxDate: undefined }
  }, [shouldRestrictDates, floorCode, floorDateRanges])

  if (buildingLoading || !buildingData) {
    return <LoadingIndicator />
  }

  const onHoverFloor = (hoverOption: IViewSelectorOption | null) => {
    setHoverFloor(hoverOption ? hoverOption.Id : null)
  }

  const viewSelectorFloorOptions: IViewSelectorOption[] = buildingData.floors
    .map((floor: { name: string; floor_code: string }) => ({
      Id: floor.floor_code,
      Content: floor.name,
      Options: [],
    }))
    .reverse()

  const currentSelectedFloor = viewSelectorFloorOptions.filter(
    option => option.Id === buildingState.floorId,
  )[0]

  const currentHoverFloor = viewSelectorFloorOptions.filter(
    option => option.Id === hoverFloor,
  )[0]

  const onClickBuildingOverviewFloor = (floor: string) => {
    collapseFloorListOnMobile()
    navigateToFloor(floor)
  }

  const viewingItems = {
    Id: 'root',
    Content: 'Floor',
    Options: viewSelectorFloorOptions,
    OnSelectChild: (selectedChild: IViewSelectorOption) =>
      onClickBuildingOverviewFloor(selectedChild.Id),
    OnHoverChild: onHoverFloor,
  }

  const onClickSelectItem = (selectedOption: IViewSelectorOption) => {
    collapseFloorListOnMobile()
    if (viewingItems.OnSelectChild) {
      viewingItems.OnSelectChild(selectedOption)
    }
  }

  const onHover = (hoverOption: IViewSelectorOption | null) => {
    if (viewingItems.OnHoverChild) {
      viewingItems.OnHoverChild(hoverOption)
    }
  }

  const siteViewFloorSelector = (
    <ViewSelector
      title="View Selector"
      initialViewSelectorExpanded
      viewingExpanded={floorListExpanded}
      setViewingExpanded={setFloorListExpanded}
      maxHeight="calc(100vh - 155px)"
    >
      <OptionPicker
        expanded={floorListExpanded}
        setExpanded={setFloorListExpanded}
        viewingItems={viewingItems}
        selectedItem={currentSelectedFloor}
        hoverItem={currentHoverFloor}
        onSelectOption={onClickSelectItem}
        onHover={onHover}
        showMarker={true}
        emptySelectionLabel="Floor Select"
      />
    </ViewSelector>
  )

  let points = <></>

  if (floorPlanViewLoaded) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper point type
    points = buildingState.floorData.points.map((point: any) => (
      <MapPoint
        key={point.point_id}
        selectedDateRange={buildingState.dateRange}
        point={point}
        highlighted={point.point_id === buildingState.pointData?.point_id}
        x={point.x}
        y={point.y}
      />
    ))
  }

  return (
    <>
      <BuildingChrome>
        <ViewModeMenuWrapper>
          <Switch>
            <Route
              path={[
                PATH_STRINGS.project,
                PATH_STRINGS.safetyOverview,
                PATH_STRINGS.safetyAlerts,
                PATH_STRINGS.safetyGallery,
                PATH_STRINGS.safetyGalleryMap,
                PATH_STRINGS.progressOverview,
                PATH_STRINGS.progressOverviewGantt,
                PATH_STRINGS.progressOverviewCharts,
                PATH_STRINGS.progressOverviewReports,
                PATH_STRINGS.progressOverviewAlerts,
                PATH_STRINGS.reviewOverview,
                PATH_STRINGS.reviewTaskFloor,
                PATH_STRINGS.reportsNew,
                PATH_STRINGS.reportsNewSafety,
                PATH_STRINGS.reportsNewProgress,
                PATH_STRINGS.reportsOverview,
                PATH_STRINGS.inspectionOverview,
                PATH_STRINGS.inspectionGallery,
                PATH_STRINGS.qualityOverview,
              ]}
              exact
            >
              <ViewModeMenu items={VIEW_MODE_MAP} zIndex={100} />
            </Route>
          </Switch>
        </ViewModeMenuWrapper>
      </BuildingChrome>
      <Suspense fallback={<LoadingIndicator />}>
        <Switch>
          <PermissionRoute
            permission="progress_tables.read_project_tracker"
            path={PATH_STRINGS.reportsNewProgress}
            component={ProgressReportGeneratorPage}
          />
          <PermissionRoute
            permission="progress_tables.read_project_tracker"
            path={PATH_STRINGS.reportsNewSafety}
            component={ReportGeneratorPage}
          />
          <PermissionRoute
            permission="progress_tables.read_project_tracker"
            exact
            path={PATH_STRINGS.reportsNew}
            component={ReportTypeChooserPage}
          />
          <PermissionRoute
            permission="progress_tables.read_project_tracker"
            path={PATH_STRINGS.reportsOverview}
            component={ReportsPage}
            projectPublicId={buildingState.projectId}
          />
          <PermissionRoute
            permission="progress_tables.read_project_tracker"
            path={PATH_STRINGS.progressOverview}
            component={ProgressPage}
          />
          <Route exact path={PATH_STRINGS.reviewOverview}>
            {userState.user_type === 'super' ? (
              <Redirect
                to={generateAdminManageReviewPath(buildingState.projectId)}
              />
            ) : (
              <PermissionWrapper permission="progress_tables.access_progress_review">
                <ReviewTaskList variant="worker" />
              </PermissionWrapper>
            )}
          </Route>
          <Route exact path={PATH_STRINGS.reviewTaskPoint}>
            <PermissionWrapper permission="progress_tables.access_progress_review">
              <ReviewTaskWorkspace />
            </PermissionWrapper>
          </Route>
          <Route exact path={PATH_STRINGS.reviewTaskFloor}>
            <PermissionWrapper permission="progress_tables.access_progress_review">
              <ReviewTaskFloorView />
            </PermissionWrapper>
          </Route>
          <Route exact path={PATH_STRINGS.reviewTask}>
            <PermissionWrapper permission="progress_tables.access_progress_review">
              <ReviewTaskFloorView />
            </PermissionWrapper>
          </Route>
          <PermissionRoute
            permission="safety_tables.read_observation"
            path={PATH_STRINGS.safetyOverview}
            component={SafetyPage}
          />
          <PermissionRoute
            permission="mission_tables.inspection_client"
            path={PATH_STRINGS.inspectionOverview}
            component={InspectionPage}
            projectPublicId={buildingState.projectId}
          />
          {areQualityFeaturesEnabled && (
            <PermissionRoute
              permission="safety_tables.read_quality_observation"
              path={PATH_STRINGS.qualityOverview}
              component={QualityPage}
              projectPublicId={buildingState.projectId}
            />
          )}
          <Route exact path={PATH_STRINGS.project}>
            <SiteViewLayout>
              <BuildingOverview
                hoverFloor={hoverFloor}
                setHoverFloor={setHoverFloor}
                onFloorClicked={onClickBuildingOverviewFloor}
                showLogos={!userState.is_demo_account}
              />
              <SiteViewFloorSelector $fullscreen={buildingState.fullscreen}>
                {siteViewFloorSelector}
              </SiteViewFloorSelector>
            </SiteViewLayout>
          </Route>
          <Route path={PATH_STRINGS.floor}>
            <SiteViewLayout>
              <FloorViewContent>
                {floorImagesLoading && <LoadingIndicator />}
                {floorPlanViewLoaded && (
                  <FloorPlanView
                    floorData={buildingState.floorData}
                    mapViewerChildren={points}
                    zoomInIconStyle={{
                      position: 'fixed',
                      right: 'max(16px, env(safe-area-inset-right, 0px))',
                      top: 'calc(50% - 44px - env(safe-area-inset-top, 0px))',
                      zIndex: 2,
                    }}
                    zoomOutIconStyle={{
                      position: 'fixed',
                      right: 'max(16px, env(safe-area-inset-right, 0px))',
                      top: 'calc(50% + 4px - env(safe-area-inset-top, 0px))',
                      zIndex: 2,
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                )}
                {isNon360ImageFloor &&
                  buildingState.pointId &&
                  buildingState.floorId &&
                  selectedFlatSubPointId != null && (
                    <FlatImagePanel
                      projectId={buildingState.projectId}
                      floorCode={buildingState.floorId}
                      subPointId={selectedFlatSubPointId}
                      flatImage={selectedFlatImage}
                      flatImages={selectedFlatPointImages}
                      flatImageIndex={Math.min(
                        flatImageIndex,
                        Math.max(selectedFlatPointImages.length - 1, 0),
                      )}
                      onSelectFlatImageIndex={onSelectFlatImageIndex}
                      loading={
                        floorImagesLoading &&
                        !buildingState.floorData?.points?.length
                      }
                      onClose={() => navigateToFloor(buildingState.floorId)}
                      onUploadSuccess={onFlatImageUploadSuccess}
                    />
                  )}
                {!isNon360ImageFloor &&
                  buildingState.imageData &&
                  buildingState.pointId &&
                  !buildingState.fullscreen && (
                    <PreviewPopup
                      imageLoading={imagesQuery.isLoading}
                      onFullScreenClick={() => {
                        navigateToCurrentPointFullscreen()
                      }}
                      onClose={() => navigateToFloor(buildingState.floorId)}
                    />
                  )}
                {!isNon360ImageFloor &&
                  buildingState.fullscreen &&
                  buildingState.floorData &&
                  buildingState.pointData &&
                  buildingState.imageData &&
                  !floorImagesLoading &&
                  Object.entries(buildingState.imageData).length !== 0 && (
                    <TagProvider>
                      <>
                        <ImageViewer minDate={minDate} maxDate={maxDate} />
                      </>
                    </TagProvider>
                  )}
              </FloorViewContent>
              <SiteViewFloorSelector
                $floorRoute
                $fullscreen={buildingState.fullscreen}
              >
                {siteViewFloorSelector}
              </SiteViewFloorSelector>
            </SiteViewLayout>
          </Route>
        </Switch>
      </Suspense>
    </>
  )
}

const SiteViewLayout = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  @media ${media.belowTablet} {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`

const FloorViewContent = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 131px);

  @media ${media.belowTablet} {
    order: 2;
    height: auto;
    min-height: 50vh;
  }
`

const SiteViewFloorSelector = styled.div<{
  $fullscreen: boolean
  $floorRoute?: boolean
}>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${props => (props.$fullscreen ? 1 : 11)};

  @media ${media.belowTablet} {
    position: static;
    z-index: 1;
    width: 100%;
    order: ${props => (props.$floorRoute ? 1 : 2)};
  }
`

const BuildingChrome = styled.div`
  width: 100%;
  box-sizing: border-box;

  @media ${media.belowTablet} {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`

const ViewModeMenuWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
  margin-top: 38px;
  width: 100%;
  box-sizing: border-box;

  @media ${media.belowTablet} {
    order: 1;
    margin-top: 0;
    margin-bottom: 0;
    padding: 0 4px;
  }
`
